import { COGNITO_CONFIG } from '@/config/cognito'
import { API_BASE_URL } from './api'

interface CognitoTokens {
  id_token?: string
  access_token?: string
  expires_at?: number
}

const TOKEN_KEY = 'qmi_tokens'

export class CognitoAuth {
  private tokens: CognitoTokens = {}

  constructor() {
    this.loadTokens()
    this.handleRedirect()
  }

  private loadTokens() {
    try {
      const stored = localStorage.getItem(TOKEN_KEY)
      if (stored) {
        this.tokens = JSON.parse(stored)
      }
    } catch (e) {
      console.error('Failed to load tokens:', e)
      this.clearTokens()
    }
  }

  private saveTokens() {
    try {
      localStorage.setItem(TOKEN_KEY, JSON.stringify(this.tokens))
    } catch (e) {
      console.error('Failed to save tokens:', e)
    }
  }

  private clearTokens() {
    this.tokens = {}
    localStorage.removeItem(TOKEN_KEY)
  }

  private handleRedirect() {
    const hash = window.location.hash
    const search = window.location.search
    
    // Check for error parameters in URL
    if (search.includes('error=') || hash.includes('error=')) {
      const urlParams = new URLSearchParams(search)
      const hashParams = new URLSearchParams(hash.substring(1))
      
      const error = urlParams.get('error') || hashParams.get('error')
      const errorDescription = urlParams.get('error_description') || hashParams.get('error_description')
      
      console.error('OAuth Error:', error, errorDescription)
      return
    }
    
    if (!hash || hash.length < 2) return

    try {
      const params = new URLSearchParams(hash.substring(1))
      const id_token = params.get('id_token')
      const access_token = params.get('access_token')
      const expires_in = parseInt(params.get('expires_in') || '0', 10)

      if (id_token || access_token) {
        this.tokens = {
          id_token: id_token || undefined,
          access_token: access_token || undefined,
          expires_at: expires_in ? Math.floor(Date.now() / 1000) + expires_in : 0
        }
        this.saveTokens()
        
        // Clear hash from URL
        window.history.replaceState(null, '', window.location.pathname + window.location.search)
      }
    } catch (e) {
      console.error('Failed to parse tokens from hash:', e)
    }
  }

  isAuthenticated(): boolean {
    if (!this.tokens.id_token) return false
    
    if (this.tokens.expires_at && Math.floor(Date.now() / 1000) >= this.tokens.expires_at) {
      this.clearTokens()
      return false
    }
    
    return true
  }

  getIdToken(): string | null {
    return this.isAuthenticated() ? this.tokens.id_token || null : null
  }

  getAccessToken(): string | null {
    return this.isAuthenticated() ? this.tokens.access_token || null : null
  }

  decodeIdToken(): any {
    const token = this.getIdToken()
    if (!token) return null

    try {
      const base64Url = token.split('.')[1]
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      )
      return JSON.parse(jsonPayload)
    } catch (e) {
      console.error('Failed to decode ID token:', e)
      return null
    }
  }

  async signIn(email: string, password: string): Promise<void> {
    console.log('Attempting sign in for:', email)
    
    try {
      const response = await fetch('https://cognito-idp.us-east-1.amazonaws.com/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-amz-json-1.1',
          'X-Amz-Target': 'AWSCognitoIdentityProviderService.InitiateAuth'
        },
        body: JSON.stringify({
          ClientId: COGNITO_CONFIG.userPoolWebClientId,
          AuthFlow: 'USER_PASSWORD_AUTH',
          AuthParameters: {
            USERNAME: email,
            PASSWORD: password
          }
        })
      })

      console.log('Response status:', response.status)
      const result = await response.json()
      console.log('Response body:', result)

      if (!response.ok) {
        throw new Error(result.__type || result.message || 'Authentication failed')
      }
      
      if (result.AuthenticationResult) {
        console.log('Authentication successful')
        this.tokens = {
          id_token: result.AuthenticationResult.IdToken,
          access_token: result.AuthenticationResult.AccessToken,
          expires_at: Math.floor(Date.now() / 1000) + result.AuthenticationResult.ExpiresIn
        }
        this.saveTokens()
        
        // Add current subdomain to Cognito URLs if it's a subdomain
        const hostname = window.location.hostname
        if (hostname !== 'admin.queuemein.com' && hostname.endsWith('.queuemein.com')) {
          const subdomain = hostname.replace('.queuemein.com', '')
          try {
            await fetch(`${API_BASE_URL}/admin/sync-cognito-urls`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${this.tokens.id_token}`
              },
              body: JSON.stringify({
                action: 'add',
                subdomain: subdomain
              })
            })
          } catch (e) {
            console.warn('Failed to sync Cognito URLs:', e)
          }
        }
        
        // Trigger a page reload to update the auth state
        window.location.reload()
      } else {
        throw new Error('Authentication failed - no result')
      }
    } catch (error) {
      console.error('Sign in error:', error)
      throw error
    }
  }

  login() {
    const currentOrigin = window.location.origin + '/'
    const clientId = COGNITO_CONFIG.userPoolWebClientId
    const responseType = COGNITO_CONFIG.oauth.responseType
    const scope = COGNITO_CONFIG.oauth.scope.join(' ')
    const domain = COGNITO_CONFIG.oauth.domain
    
    console.log('Login called with config:', { clientId, domain, currentOrigin })
    
    if (!clientId || !domain) {
      console.error('Missing Cognito configuration')
      return
    }
    
    const loginUrl = `https://${domain}/oauth2/authorize?client_id=${encodeURIComponent(clientId)}&response_type=${encodeURIComponent(responseType)}&scope=${encodeURIComponent(scope)}&redirect_uri=${encodeURIComponent(currentOrigin)}`
    window.location.assign(loginUrl)
  }

  logout() {
    this.clearTokens()
    
    // Use current domain to preserve subdomain
    const currentOrigin = window.location.origin + '/'
    
    const params = new URLSearchParams({
      client_id: COGNITO_CONFIG.userPoolWebClientId,
      logout_uri: currentOrigin
    })

    // Redirect to Cognito logout to properly clear session
    const logoutUrl = `https://${COGNITO_CONFIG.oauth.domain}/logout?${params.toString()}`
    window.location.assign(logoutUrl)
  }

  forgotPassword() {
    // Use current domain to preserve subdomain
    const currentOrigin = window.location.origin + '/'
    
    const params = new URLSearchParams({
      client_id: COGNITO_CONFIG.userPoolWebClientId,
      response_type: COGNITO_CONFIG.oauth.responseType,
      scope: COGNITO_CONFIG.oauth.scope.join(' '),
      redirect_uri: currentOrigin
    })

    // Redirect to Cognito Hosted UI forgot password page
    const forgotPasswordUrl = `https://${COGNITO_CONFIG.oauth.domain}/forgotPassword?${params.toString()}`
    window.location.assign(forgotPasswordUrl)
  }

  getUserInfo() {
    const payload = this.decodeIdToken()
    if (!payload) return null

    const groups = payload['cognito:groups'] || []
    const groupsArray = Array.isArray(groups) ? groups : 
                      typeof groups === 'string' ? groups.split(',').map(g => g.trim()) : []

    return {
      email: payload.email,
      sub: payload.sub,
      groups: groupsArray,
      tenant_from_group: this.extractTenantFromGroups(groupsArray)
    }
  }

  private extractTenantFromGroups(groups: string[]): string | null {
    for (const group of groups) {
      if (group.startsWith('TENANT_ADMIN__')) {
        return group.split('TENANT_ADMIN__')[1]
      }
      if (group.startsWith('TENANT_MANAGER__')) {
        return group.split('TENANT_MANAGER__')[1]
      }
    }
    return null
  }
}

export const cognitoAuth = new CognitoAuth()